import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Skeleton } from "../components/Loader";
import DatePicker from "react-datepicker";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  fetchOnlineSalesReport,
  selectOnlineSalesData,
  selectOnlineSalesError,
  selectOnlineSalesStatus,
} from "../features/reports/reportsSlice";
import { fetchStores, selectStores, selectStoresStatus } from "../features/stores/storesSlice";
import StoreMultiSelect from "../components/StoreMultiSelect";

import "./ReportPage.css";

/**
 * Страница «Online Продажи» — замена дашборда Metabase `6-online-prodazh` (AUFB-65).
 *
 * Панели дашборда один в один: сумма онлайн-продаж, их количество, сумма скидок,
 * количество проданных товаров по магазинам, таблица товаров и график по дням.
 * Данные — `/api/reports/online-sales/` (`payment_type=ONLINE`, `status=PAID`).
 */

function formatMoney(value) {
  const num = Number(value || 0);
  if (Number.isNaN(num)) return "0.00";
  return num.toLocaleString("ru-RU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatCount(value) {
  const num = Number(value || 0);
  if (Number.isNaN(num)) return "0";
  return num.toLocaleString("ru-RU");
}

/** Штуки: у количества дробная часть есть только у весового товара, целые не «.00». */
function formatQuantity(value) {
  const num = Number(value || 0);
  if (Number.isNaN(num)) return "0";
  return Number.isInteger(num) ? num.toLocaleString("ru-RU") : num.toLocaleString("ru-RU", { maximumFractionDigits: 3 });
}

function formatAxisMoney(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 10_000) return `${(n / 1000).toFixed(0)}k`;
  return String(Math.round(n));
}

function formatAxisDate(v) {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function formatTooltipDate(v) {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function toIsoLocal(d) {
  if (!d) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function parseIsoLocal(iso) {
  if (!iso || typeof iso !== "string") return null;
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (!y || !mo || !d) return null;
  return new Date(y, mo - 1, d);
}

/**
 * Начало текущей недели (понедельник) — дашборд открывается с фильтром
 * «Дата: На этой неделе», сохраняем это поведение.
 */
function startOfWeekIso() {
  const now = new Date();
  const shift = (now.getDay() + 6) % 7; // 0 = понедельник
  now.setDate(now.getDate() - shift);
  return toIsoLocal(now);
}

function todayIso() {
  return toIsoLocal(new Date());
}

function KpiCard({ title, value, hint, accent }) {
  return (
    <div className="kpi-card">
      <div className="kpi-title">{title}</div>
      <div className={accent ? `kpi-value ${accent}` : "kpi-value"}>{value}</div>
      {hint && <div className="kpi-hint">{hint}</div>}
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div className="section">
      <div className="section-head">
        <div className="section-title">{title}</div>
        {subtitle && <div className="section-subtitle">{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}

function Empty({ text = "Нет данных за выбранный период" }) {
  return <div className="empty">{text}</div>;
}

function BreakdownTable({ rows, columns, emptyText }) {
  if (!rows || rows.length === 0) return <Empty text={emptyText} />;
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key} className={col.numeric ? "numeric" : undefined}>
                {col.title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={row.id ?? index}>
              {columns.map((col) => {
                const raw = row[col.key];
                let content = raw;
                if (col.money) content = formatMoney(raw);
                else if (col.quantity) content = formatQuantity(raw);
                else if (col.numeric) content = formatCount(raw);
                else if (raw === null || raw === undefined || raw === "") content = "—";
                const cellClasses = [col.numeric ? "numeric" : "", col.bold ? "bold" : ""].filter(Boolean).join(" ");
                return (
                  <td key={col.key} className={cellClasses || undefined}>
                    {content}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="hint-block">{rows.length} строк</div>
    </div>
  );
}

const tooltipStyle = {
  background: "var(--c-card)",
  border: "1px solid var(--c-border)",
  borderRadius: 8,
  color: "var(--c-text)",
  fontSize: 14,
};

export default function OnlineSalesPage() {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const stores = useSelector(selectStores);
  const storesStatus = useSelector(selectStoresStatus);

  const data = useSelector(selectOnlineSalesData);
  const status = useSelector(selectOnlineSalesStatus);
  const error = useSelector(selectOnlineSalesError);

  const [storeIds, setStoreIds] = useState(() => {
    const raw = searchParams.getAll("store_ids");
    return raw.length ? raw.map(String) : [];
  });
  const [startDate, setStartDate] = useState(() => searchParams.get("start_date") || startOfWeekIso());
  const [endDate, setEndDate] = useState(() => searchParams.get("end_date") || todayIso());

  useEffect(() => {
    if (storesStatus === "idle") dispatch(fetchStores({ pageSize: 200 }));
  }, [dispatch, storesStatus]);

  const load = useCallback(() => {
    const numericIds = storeIds.map((id) => Number(id)).filter((id) => Number.isFinite(id));
    dispatch(fetchOnlineSalesReport({ storeIds: numericIds, startDate, endDate }));
  }, [dispatch, storeIds, startDate, endDate]);

  useEffect(() => {
    load();
  }, [load]);

  // Фильтры держим в URL — ссылку на период можно переслать.
  useEffect(() => {
    const params = new URLSearchParams();
    storeIds.forEach((id) => params.append("store_ids", String(id)));
    if (startDate) params.set("start_date", startDate);
    if (endDate) params.set("end_date", endDate);
    setSearchParams(params, { replace: true });
  }, [storeIds, startDate, endDate, setSearchParams]);

  const handleReset = () => {
    setStoreIds([]);
    setStartDate(startOfWeekIso());
    setEndDate(todayIso());
  };

  const totals = data?.totals;

  const storeRows = useMemo(
    () => (data?.by_store || []).map((row) => ({ ...row, id: row.store_id })),
    [data]
  );

  const productRows = useMemo(
    () => (data?.top_products || []).map((row, i) => ({ ...row, id: row.product_id ?? i })),
    [data]
  );

  const dailyRows = useMemo(
    () =>
      (data?.daily || []).map((row) => ({
        ...row,
        revenue_num: Number(row.revenue || 0),
      })),
    [data]
  );

  return (
    <div className="sales-dashboard-page">
      <div className="header">
        <div>
          <div className="title">Online Продажи</div>
          <div className="subtitle">Продажи с оплатой онлайн за выбранный период</div>
        </div>
      </div>

      <div className="toolbar">
        <StoreMultiSelect stores={stores} value={storeIds} onChange={setStoreIds} />
        <div className="field">
          <label className="label">С даты</label>
          <DatePicker
            selected={parseIsoLocal(startDate)}
            onChange={(d) => setStartDate(toIsoLocal(d))}
            dateFormat="dd.MM.yyyy"
            placeholderText="дд.мм.гггг"
            customInput={<input className="input" />}
            wrapperClassName="pnl-datepicker-wrapper"
          />
        </div>
        <div className="field">
          <label className="label">По дату</label>
          <DatePicker
            selected={parseIsoLocal(endDate)}
            onChange={(d) => setEndDate(toIsoLocal(d))}
            dateFormat="dd.MM.yyyy"
            placeholderText="дд.мм.гггг"
            customInput={<input className="input" />}
            wrapperClassName="pnl-datepicker-wrapper"
          />
        </div>
        <div className="toolbar-buttons">
          <button type="button" onClick={load} className="apply-btn">
            Обновить
          </button>
          <button type="button" onClick={handleReset} className="reset-btn">
            Сбросить
          </button>
        </div>
      </div>

      {status === "loading" && <Skeleton variant="cards" count={4} text="Загрузка отчёта…" style={{ padding: "16px 24px 32px" }} />}
      {status === "failed" && error && <div className="error-box">{error}</div>}

      <div className="body">
        <div className="kpi-grid">
          <KpiCard title="Сумма Online продаж" value={formatMoney(totals?.revenue)} accent="accent" />
          <KpiCard title="Количество Online продаж" value={formatCount(totals?.sale_count)} />
          <KpiCard title="Сумма скидок" value={formatMoney(totals?.discount_total)} />
          <KpiCard
            title="Возвраты"
            value={formatMoney(totals?.refund_revenue)}
            accent="danger"
            hint={`${formatCount(totals?.refund_count)} шт.`}
          />
        </div>

        <Section title="Количество Online проданных товаров" subtitle="По магазинам">
          <BreakdownTable
            rows={storeRows}
            columns={[
              { key: "store_name", title: "Магазин" },
              { key: "quantity", title: "Количество", numeric: true, quantity: true },
              { key: "revenue", title: "Сумма Sales Price", numeric: true, money: true, bold: true },
            ]}
          />
        </Section>

        <Section title="Количество проданных товаров" subtitle="По товарам">
          <BreakdownTable
            rows={productRows}
            columns={[
              { key: "product_name", title: "Название продукта" },
              { key: "unit_price", title: "Цена (сом)", numeric: true, money: true },
              { key: "quantity", title: "Количество", numeric: true, quantity: true },
              { key: "revenue", title: "Сумма Продаж", numeric: true, money: true, bold: true },
            ]}
            emptyText="За период не продано ни одного товара"
          />
        </Section>

        <Section title="График Online продаж">
          {dailyRows.length === 0 ? (
            <Empty />
          ) : (
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={dailyRows} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--c-border)" />
                <XAxis dataKey="date" tickFormatter={formatAxisDate} stroke="var(--c-text-muted)" fontSize={12} />
                <YAxis tickFormatter={formatAxisMoney} stroke="var(--c-text-muted)" fontSize={12} />
                <Tooltip
                  contentStyle={tooltipStyle}
                  labelFormatter={formatTooltipDate}
                  formatter={(value) => [formatMoney(value), "Сумма продаж"]}
                />
                <Line
                  type="monotone"
                  dataKey="revenue_num"
                  name="Сумма продаж"
                  stroke="var(--c-s3)"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Section>
      </div>
    </div>
  );
}
